import type { AgentTool } from '@saaga/shared/interfaces/agent-tool.interface'
import { type ReactNode, useMemo } from 'react'

import { CommandEmpty, CommandGroup, CommandList } from '@/components/ui/command'
import { getFilteredSelectedCount } from './tool-selector.helper'
import { ToolSelectorItem } from './tool-selector-item.atom'
import { ToolSelectorSelectAll } from './tool-selector-select-all.atom'

export interface ToolSelectorCommandContentProps {
  toolList: AgentTool[]
  filteredToolList: AgentTool[]
  selectedIdList: string[]
  onToggle: (toolId: string) => void
  onSelectAll?: (filteredIdList: string[]) => void
  onDeselectAll?: (filteredIdList: string[]) => void
}

/**
 * Command list content for the tool selector.
 * Displays tools with checkboxes for multi-select. Includes a "Select All" option at the top.
 */
export const ToolSelectorCommandContent = ({
  toolList,
  filteredToolList,
  selectedIdList,
  onToggle,
  onSelectAll,
  onDeselectAll,
}: ToolSelectorCommandContentProps): ReactNode => {
  const hasSelectAll = filteredToolList.length > 0 && onSelectAll && onDeselectAll
  const filteredIdList = useMemo(() => filteredToolList.map((tool) => tool.id), [filteredToolList])
  const filteredSelectedCount = useMemo(
    () => getFilteredSelectedCount(selectedIdList, filteredIdList),
    [selectedIdList, filteredIdList]
  )

  return (
    <>
      {hasSelectAll && (
        <ToolSelectorSelectAll
          selectedCount={filteredSelectedCount}
          totalCount={filteredToolList.length}
          onSelectAll={() => onSelectAll(filteredIdList)}
          onDeselectAll={() => onDeselectAll(filteredIdList)}
        />
      )}
      <CommandList className="tool-selector-list max-h-[300px]">
        <CommandEmpty>No tools found.</CommandEmpty>
        <CommandGroup>
          {toolList.map((tool) => (
            <ToolSelectorItem
              key={tool.id}
              tool={tool}
              isSelected={selectedIdList.includes(tool.id)}
              onSelect={onToggle}
            />
          ))}
        </CommandGroup>
      </CommandList>
    </>
  )
}

ToolSelectorCommandContent.displayName = 'ToolSelectorCommandContent'
